import { jsPDF } from "jspdf";
import { LC_SHIELD_PDF_LOGO } from "@/lib/pdfLogo";

const SEV_ORDER = { critical: 0, warning: 1, info: 2 };
const CJK_URL = "https://cdn.jsdelivr.net/gh/StellarCN/scp_zh@master/fonts/SimHei.ttf";

// Clean, standardized UTC timestamp e.g. "2026/08/28 12:43:34 UTC" (no locale artifacts).
function utcStamp(d = new Date()) {
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getUTCFullYear()}/${p(d.getUTCMonth() + 1)}/${p(d.getUTCDate())} ${p(d.getUTCHours())}:${p(d.getUTCMinutes())}:${p(d.getUTCSeconds())} UTC`;
}

// English defaults (also the PDF fallback if a CJK font can't be embedded).
const EN = {
  title: "Presentation Readiness Report",
  generated: "Generated {date}  ·  Ref {ref}  ·  Engine {model}",
  ready: "PRESENTATION READY",
  notReady: "NOT READY — DISCREPANCIES FOUND",
  score: "Readiness Score",
  critical: "Critical", warnings: "Warnings", notes: "Notes",
  discrepancies: "DISCREPANCIES",
  document: "Document", lcRequires: "LC requires:", docShows: "Document shows:",
  reference: "Reference", suggestedFix: "Suggested fix", checksPassed: "CHECKS PASSED",
  sev: { critical: "Critical", warning: "Warning", info: "Note" },
  disclaimer: "DISCLAIMER: LC Shield is an automated document verification assistant operating under ICC UCP 600 & ISBP 821 (2023) rules. It provides non-binding workflow assistance and does not constitute legal advice, banking guarantee, or official document acceptance by issuing banks.",
  csv: ["Severity", "Title", "Document", "LC Requirement", "Document Value", "UCP Reference", "Suggested Fix"],
  redactedBadge: "Audited on Client-Side Redacted Input (Privacy-First)",
};

let _cjkB64 = null;
async function loadCjkBase64() {
  if (_cjkB64) return _cjkB64;
  const res = await fetch(CJK_URL);
  if (!res.ok) throw new Error("font fetch failed");
  const bytes = new Uint8Array(await res.arrayBuffer());
  let bin = "";
  const chunk = 8192;
  for (let i = 0; i < bytes.length; i += chunk) bin += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  _cjkB64 = btoa(bin);
  return _cjkB64;
}

export function exportCSV(result, meta = {}, { labels, redacted } = {}) {
  const L = labels || EN;
  const sev = L.sev || EN.sev;
  const rows = [];
  if (redacted) rows.push([L.redactedBadge || EN.redactedBadge]);
  rows.push(L.csv || EN.csv);
  (result.discrepancies || [])
    .slice()
    .sort((a, b) => (SEV_ORDER[a.severity] ?? 3) - (SEV_ORDER[b.severity] ?? 3))
    .forEach((d) => {
      rows.push([
        sev[d.severity] || d.severity, d.title, d.document, d.lc_requirement, d.document_value, d.ucp_reference, d.suggested_fix,
      ]);
    });
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  // BOM so Excel renders UTF-8 (incl. Chinese) correctly.
  const csv = "\uFEFF" + rows.map((r) => r.map(esc).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `lc-shield-report-${(meta.id || "export").slice(0, 8)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export async function exportPDF(result, meta = {}, { labels, lang, redacted } = {}) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  const M = 48;
  let y = 56;

  // Resolve font + labels. CJK languages embed a Unicode font; fall back to English if it fails.
  let bodyFont = "helvetica";
  let L = EN;
  if (String(lang || "").startsWith("zh") && labels) {
    try {
      const b64 = await loadCjkBase64();
      doc.addFileToVFS("cjk.ttf", b64);
      doc.addFont("cjk.ttf", "cjk", "normal");
      bodyFont = "cjk";
      L = labels;
    } catch {
      bodyFont = "helvetica";
      L = EN;
    }
  } else if (labels) {
    L = labels;
  }

  const line = (txt, size, color, font = bodyFont, style = "normal") => {
    const f = font === "cjk" ? "cjk" : font;
    const s = f === "cjk" ? "normal" : style; // embedded CJK font is normal-weight only
    doc.setFont(f, s);
    doc.setFontSize(size);
    doc.setTextColor(...color);
    const lines = doc.splitTextToSize(String(txt ?? ""), W - M * 2);
    lines.forEach((l) => {
      if (y > 780) { doc.addPage(); y = 56; }
      doc.text(l, M, y);
      y += size + 4;
    });
  };

  // Header band + official shield logo (falls back to vector monogram if the image can't load).
  doc.setFillColor(11, 17, 32);
  doc.rect(0, 0, W, 34, "F");
  let wordmarkX = 66;
  try {
    const lh = 25, lw = lh * (131 / 150); // preserve shield aspect ratio
    doc.addImage(LC_SHIELD_PDF_LOGO, "PNG", 16, 4.5, lw, lh);
    wordmarkX = 16 + lw + 9;
  } catch {
    doc.setDrawColor(56, 189, 248);
    doc.setLineWidth(1.1);
    doc.setLineJoin("round");
    doc.setLineCap("round");
    doc.lines([[14, 0], [0, 9], [-7, 9], [-7, -9], [0, -9]], 44, 9, [1, 1], "S", true);
    doc.setLineWidth(1.3);
    doc.lines([[0, 8], [4, 0]], 48.5, 13, [1, 1], "S", false);
    const cPts = [[55.2, 21.1], [53, 21.9], [50.8, 21.1], [49.6, 18.5], [50.8, 15.9], [53, 15.1], [55.2, 15.9]];
    for (let i = 0; i < cPts.length - 1; i++) doc.line(cPts[i][0], cPts[i][1], cPts[i + 1][0], cPts[i + 1][1]);
    wordmarkX = 66;
  }
  doc.setFont("times", "bold");
  doc.setFontSize(15);
  doc.setTextColor(56, 189, 248);
  doc.text("LC Shield", wordmarkX, 22);
  doc.setTextColor(148, 163, 184);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text("getlcshield.com", W - M, 22, { align: "right" });

  y = 68;
  line(L.title, 20, [15, 23, 42], bodyFont, "bold");
  line(
    L.generated
      .replace("{date}", utcStamp())
      .replace("{ref}", (meta.id || "").slice(0, 8))
      .replace("{model}", meta.model || "claude-sonnet-4-6"),
    9, [100, 116, 139]
  );
  y += 6;
  if (redacted) {
    line(L.redactedBadge || EN.redactedBadge, 8.5, [16, 129, 90], bodyFont, "bold");
    y += 4;
  }

  const c = result.counts || {};
  line(
    `${L.score}: ${result.readiness_score ?? "-"}/100    |    ${result.presentation_ready ? L.ready : L.notReady}`,
    12, result.presentation_ready ? [16, 185, 129] : [220, 38, 38], bodyFont, "bold"
  );
  line(`${L.critical}: ${c.critical ?? 0}    ${L.warnings}: ${c.warning ?? 0}    ${L.notes}: ${c.info ?? 0}`, 10, [71, 85, 105]);
  y += 4;
  line(result.overview || "", 10, [15, 23, 42]);
  y += 8;

  line(L.discrepancies, 12, [15, 23, 42], bodyFont, "bold");
  y += 2;
  (result.discrepancies || [])
    .slice()
    .sort((a, b) => (SEV_ORDER[a.severity] ?? 3) - (SEV_ORDER[b.severity] ?? 3))
    .forEach((d, i) => {
      const col = d.severity === "critical" ? [220, 38, 38] : d.severity === "warning" ? [217, 119, 6] : [14, 165, 233];
      const sevLabel = (L.sev || EN.sev)[d.severity] || d.severity;
      line(`${i + 1}. [${sevLabel}] ${d.title}`, 11, col, bodyFont, "bold");
      line(`${L.document}: ${d.document}`, 9, [71, 85, 105]);
      line(`${L.lcRequires} ${d.lc_requirement}`, 9, [71, 85, 105]);
      line(`${L.docShows} ${d.document_value}`, 9, [71, 85, 105]);
      line(`${L.reference}: ${d.ucp_reference}`, 9, [3, 105, 161]);
      line(`${L.suggestedFix}: ${d.suggested_fix}`, 9, [15, 23, 42]);
      y += 8;
    });

  if ((result.passed_checks || []).length) {
    line(L.checksPassed, 12, [15, 23, 42], bodyFont, "bold");
    y += 4;
    const items = result.passed_checks;
    const gap = 18;
    const colW = (W - M * 2 - gap) / 2;
    const size = 9, lh = size + 3, textIndent = 12;
    const colX = [M, M + colW + gap];
    const drawCheck = (x, baseline) => {
      doc.setDrawColor(16, 185, 129);
      doc.setLineWidth(1);
      doc.setLineCap("round");
      doc.setLineJoin("round");
      doc.line(x, baseline - 2.6, x + 2.4, baseline - 0.4);
      doc.line(x + 2.4, baseline - 0.4, x + 6.4, baseline - 5.6);
    };
    doc.setFont(bodyFont === "cjk" ? "cjk" : "helvetica", "normal");
    doc.setFontSize(size);
    for (let i = 0; i < items.length; i += 2) {
      const cells = [items[i], items[i + 1]].map((txt) =>
        txt != null ? doc.splitTextToSize(String(txt), colW - textIndent) : null
      );
      const rows = Math.max(...cells.map((c) => (c ? c.length : 0)));
      if (y + rows * lh > 780) { doc.addPage(); y = 56; }
      cells.forEach((cLines, ci) => {
        if (!cLines) return;
        doc.setTextColor(16, 129, 90);
        drawCheck(colX[ci], y);
        cLines.forEach((l, k) => doc.text(l, colX[ci] + textIndent, y + k * lh));
      });
      y += rows * lh + 5;
    }
    y += 8;
  }

  y += 6;
  line(L.disclaimer, 7.5, [148, 163, 184]);
  y += 4;
  line("© LC Shield · getlcshield.com · support@getlcshield.com", 7.5, [148, 163, 184], "helvetica");

  doc.save(`lc-shield-report-${(meta.id || "export").slice(0, 8)}.pdf`);
}

/* ---------- L/C Amendment Request exports ---------- */

// Strip common Markdown while preserving paragraph structure. Used for the .txt export.
export function stripMarkdown(src = "") {
  return String(src)
    .replace(/\r\n/g, "\n")
    .replace(/^#{1,6}\s+/gm, "")            // headings
    .replace(/\*\*(.+?)\*\*/g, "$1")        // bold (LLM output uses **)
    .replace(/`([^`]+)`/g, "$1")            // inline code
    .replace(/^\s*[-+]\s+/gm, "• ")         // list bullets (leave lone * / _ intact for fill-in blanks)
    .replace(/^\s*>\s?/gm, "")              // blockquotes
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1") // links → text
    .replace(/\n{3,}/g, "\n\n")             // collapse excessive blank lines
    .trim();
}

// Best-effort LC number extraction from subject/body for the export filename.
export function extractLcNumber(amendment = {}) {
  const hay = `${amendment.subject || ""}\n${amendment.letter_body || ""}`;
  const patterns = [
    /(?:L\/?C|Letter of Credit|Documentary Credit|Credit|信用[狀证证])\s*(?:No\.?|Number|号码|號碼|编号|編號|#|:)?\s*([A-Z0-9][A-Z0-9\-\/]{4,})/i,
    /\b([A-Z]{2,4}[0-9]{6,})\b/,
  ];
  for (const re of patterns) {
    const m = hay.match(re);
    if (m && m[1]) return m[1].replace(/[^A-Za-z0-9\-]/g, "").slice(0, 32);
  }
  return "";
}

function amendmentFileBase(amendment) {
  const lc = extractLcNumber(amendment);
  if (lc) return `LC_Amendment_Request_${lc}`;
  const p = (n) => String(n).padStart(2, "0");
  const d = new Date();
  return `LC_Amendment_Request_${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}`;
}

export function exportAmendmentTXT(amendment = {}) {
  // Letter body already contains the subject/"Re:" line; avoid duplicating it.
  const text = "\uFEFF" + stripMarkdown(amendment.letter_body || "");
  const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${amendmentFileBase(amendment)}.txt`;
  a.click();
  URL.revokeObjectURL(url);
}

export async function exportAmendmentPDF(amendment = {}, { labels, lang } = {}) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  const M = 48;
  let y = 56;

  const AL = {
    reqChanges: "REQUESTED AMENDMENTS",
    field: "Field", ucpRef: "UCP 600 Reference", from: "Current", to: "Requested",
    disclaimer:
      "DISCLAIMER: This amendment request is a draft prepared with LC Shield's automated assistant under ICC UCP 600 & ISBP 821 (2023) rules. It is non-binding, does not constitute legal advice, and must be reviewed and authorised by the applicant before submission to the issuing bank.",
    ...(labels || {}),
  };

  let bodyFont = "helvetica";
  if (String(lang || "").startsWith("zh")) {
    try {
      const b64 = await loadCjkBase64();
      doc.addFileToVFS("cjk.ttf", b64);
      doc.addFont("cjk.ttf", "cjk", "normal");
      bodyFont = "cjk";
    } catch {
      bodyFont = "helvetica";
    }
  }

  const line = (txt, size, color, font = bodyFont, style = "normal", indent = 0) => {
    const f = font === "cjk" ? "cjk" : font;
    const s = f === "cjk" ? "normal" : style;
    doc.setFont(f, s);
    doc.setFontSize(size);
    doc.setTextColor(...color);
    const lines = doc.splitTextToSize(String(txt ?? ""), W - M * 2 - indent);
    lines.forEach((l) => {
      if (y > 780) { doc.addPage(); y = 56; }
      doc.text(l, M + indent, y);
      y += size + 4;
    });
  };

  // Header band + official shield logo (vector monogram fallback).
  doc.setFillColor(11, 17, 32);
  doc.rect(0, 0, W, 34, "F");
  let wordmarkX = 66;
  try {
    const lh = 25, lw = lh * (131 / 150);
    doc.addImage(LC_SHIELD_PDF_LOGO, "PNG", 16, 4.5, lw, lh);
    wordmarkX = 16 + lw + 9;
  } catch {
    wordmarkX = 24;
  }
  doc.setFont("times", "bold");
  doc.setFontSize(15);
  doc.setTextColor(56, 189, 248);
  doc.text("LC Shield", wordmarkX, 22);
  doc.setTextColor(148, 163, 184);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text("getlcshield.com", W - M, 22, { align: "right" });

  y = 72;
  if (amendment.subject) {
    line(amendment.subject, 15, [15, 23, 42], bodyFont, "bold");
    y += 4;
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.8);
    doc.line(M, y, W - M, y);
    y += 16;
  }

  // Letter body (already contains salutation, body and signature block).
  line(stripMarkdown(amendment.letter_body || ""), 10.5, [15, 23, 42]);
  y += 12;

  const reqs = amendment.requested_amendments || [];
  if (reqs.length) {
    if (y > 720) { doc.addPage(); y = 56; }
    line(AL.reqChanges, 12, [15, 23, 42], bodyFont, "bold");
    y += 4;
    reqs.forEach((a, i) => {
      if (y > 740) { doc.addPage(); y = 56; }
      line(`${i + 1}. ${a.field}`, 10.5, [11, 17, 32], bodyFont, "bold");
      line(`${AL.from}: ${a.current}`, 9.5, [190, 40, 40], bodyFont, "normal", 12);
      line(`${AL.to}: ${a.requested}`, 9.5, [16, 129, 90], bodyFont, "normal", 12);
      if (a.ucp_reference) line(`${AL.ucpRef}: ${a.ucp_reference}`, 9, [3, 105, 161], bodyFont, "normal", 12);
      y += 6;
    });
    y += 6;
  }

  if (y > 720) { doc.addPage(); y = 56; }
  y += 6;
  line(AL.disclaimer, 7.5, [148, 163, 184]);
  y += 4;
  line("© LC Shield · getlcshield.com · support@getlcshield.com", 7.5, [148, 163, 184], "helvetica");

  doc.save(`${amendmentFileBase(amendment)}.pdf`);
}
